import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress, Typography, Button, Tooltip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import CheckIcon from '@material-ui/icons/Check';
import { getAssociates } from '../../state/associate/actions';
import { getAppUser } from './helpers';
import { blueGrey, grey } from '@material-ui/core/colors';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignContent: 'center',
    margin: '10px 0',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: grey[200],
    },
  },
  item: {
    marginLeft: -24,
    marginRight: -24
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: '10px 30px',
  },
  assocAvatar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  assocData: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '80%',
    height: '50px',
    marginLeft: 6,
    fontSize: 9,
    '& .assocName': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    '& .assocCpf': {
      fontSize: 14,
      color: '#999',
    }
  },
  assocInput: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    margin: '10px 0'
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
  },
  skeleton: {
    backgroundColor: '#ccc',
  },
  buttonSave: {
    paddingTop: 8,
    paddingBottom: 8,
    marginLeft: 16,
    backgroundColor: blueGrey[800]
  },
  listContent: {
    borderBottom: `1px solid ${grey[200]}`,
    borderTop: `1px solid ${grey[200]}`,
    maxHeight: '50vh',
    minWidth: 380,
    minHeight: 130
  },
  spacingBottom: {
    marginBottom: 10
  },
  action: {
    padding: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  contentProgress: {
    textAlign: 'center',
    paddingTop: theme.spacing(4)
  }
}));

export default function AssociadosModal(props) {
  const { associates, loaded, error } = useSelector(state => state.associates);
  const { open, handleClose, handleSelect } = props;
  const { loja } = getAppUser() || {};

  const classes = useStyles();
  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(getAssociates({ loja }));
  }, [dispatch, loja]);

  const [retrying, setRetrying] = React.useState(false);
  const [assoc, setAssoc] = React.useState('');
  const retry = () => {
    setRetrying(true);
    dispatch(getAssociates({ loja })).then(() => setRetrying(false));
  };

  const renderError = () => (
    <>
      {retrying && <Box flexGrow={1} className={classes.contentProgress}><CircularProgress /></Box>}
      {!retrying && <Box flexGrow={1}>
        <Typography variant="body2" className={classes.spacingBottom}>Houve algum erro ao buscar a lista de associados.</Typography>
        <Box flexGrow={1} display="flex" p={0} className={classes.spacingBottom}>
          <Box flexGrow={1}>
            <TextField
              label="Nome / CPF Associado"
              fullWidth
              value={assoc}
              onChange={(e) => setAssoc(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Box>
          <Box>
            <Tooltip title="Salvar" placement="top">
              <Button
                className={classes.buttonSave}
                color="primary"
                variant="contained"
                aria-label="salvar"
                disableElevation
                onClick={() => handleSelect(assoc, true)}>
                <CheckIcon />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>}
    </>
  );

  const renderAssociate = (a) => (
    <div
      key={a.cpf}
      className={clsx(classes.container, classes.item)}
      onClick={() => (handleSelect instanceof Function) && handleSelect(a)}
    >
      <div className={classes.assocAvatar}>
        <Avatar alt={a.nome} className={classes.avatar} />
      </div>
      <div className={classes.assocData}>
        <Typography className="assocName">{a.nomeCompleto || a.nome}</Typography>
        <Typography className="assocCpf">{a.prontuario} ({a.entregas} entregas)</Typography>
      </div>
    </div>
  );

  const renderSkeleton = (i) => (
    <div key={i} className={classes.container}>
      <span className={classes.assocAvatar}>
        <Skeleton variant="circle" animation="wave" width={40} height={40} className={classes.skeleton} />
      </span>
      <span className={classes.assocData}>
        <Skeleton variant="text" animation="wave" className={classes.skeleton} />
        <Skeleton variant="text" animation="wave" width="60%" className={classes.skeleton} />
      </span>
    </div>
  );

  const errorOcurred = (error || (loaded && !(associates instanceof Array)));
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>Selecione um associado</DialogTitle>

        <DialogContent className={classes.listContent}>
          {errorOcurred
            ? renderError()
            : loaded
              ? associates.sort((a,b) => b.entregas - a.entregas).map(renderAssociate)
              : [1, 2, 3, 4, 5].map(renderSkeleton)
          }
        </DialogContent>
        <DialogActions className={classes.action}>
          <Box flexGrow={1} display="flex" p={0}>
            <Box flexGrow={1} >
              <Button onClick={handleClose} color="inherit">Fechar</Button>
            </Box>
            <Box>
              {error &&
                <Button
                  variant="outlined"
                  onClick={retry}
                  color="default"
                  disableElevation>
                  Tentar novamente
              </Button>
              }
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}
